import type { ImageModel } from '@rocco/types/image-model';
import type { SearchResultItem } from '~/modules/search';
import type { ProjectBannerInfo } from '~/modules-rocco/projects/api/get-project-banner-images.server';

import type { BasicThumbnail } from '~/utils/get-tile-thumbnail-for-professional';
import { isBasicThumbnail } from '~/utils/get-tile-thumbnail-for-professional';
import { appendNoIndexParam } from '~/utils/append-noindex-param';

type projectWithThumbnail = BasicThumbnail | SearchResultItem;

/**
 * Normalises an image URL to a stable key for matching the same source image
 * across an ES tile thumb and a GraphQL image thumbnail, which differ by host
 * and by crop/transform suffix. Returns the `/images/.../file.ext` portion
 * (everything up to the file extension), dropping any host prefix and the
 * trailing transform segment.
 */
export const normalizeImageKey = (url: string): string => {
    const match = url.match(/\/images\/.+?\.(?:jpe?g|png|webp|gif)/i);
    return match ? match[0] : url;
};

/** Puts the current banner slide first so the tile carousel opens on it. */
const moveBannerImageFirst = (images: ImageModel[]): ImageModel[] => {
    const bannerIndex = images.findIndex((image) => image.isBanner);
    if (bannerIndex <= 0) {
        return images;
    }

    return [
        images[bannerIndex]!,
        ...images.slice(0, bannerIndex),
        ...images.slice(bannerIndex + 1),
    ];
};

const isSearchResultItem = (p: projectWithThumbnail): p is SearchResultItem => {
    return (p as SearchResultItem).PType !== undefined;
};

// we have many place need to maintain get the tile thumbnail from a project
// so we create this backward-compatible function to use in all places
// doc: https://archipro.atlassian.net/wiki/spaces/AD/pages/1788313607
export const getProjectTileThumbnail = (
    project: projectWithThumbnail,
    isMobile?: boolean,
): string => {
    if (isBasicThumbnail(project)) {
        const thumbnail = isMobile
            ? project.Thumbnail?.mobile
            : project.Thumbnail?.desktop;
        if (thumbnail) {
            return appendNoIndexParam(thumbnail);
        }
    }

    // check if project is a SearchResultItem
    if (isSearchResultItem(project)) {
        const thumbs = project.LibraryThumbs;
        const directoryTiles =
            project.DirectoryTiles?.filter((imageTile) => imageTile.Thumb) ??
            [];

        const preferredThumb = directoryTiles?.[0]?.Thumb;

        return appendNoIndexParam(
            preferredThumb ?? thumbs?.Thumb_category ?? thumbs?.Thumb_lg ?? '',
        );
    }

    return '';
};

/**
 * Extracts an array of ImageModel objects from the DirectoryTiles property of a SearchResultItem.
 * Filters out any tiles without a valid Thumb property.
 * This utility is used to provide consistent image data for project tiles across the application.
 *
 * @param item - The SearchResultItem containing DirectoryTiles to extract images from.
 * @param bannerInfo - Optional per-project banner data (from the GraphQL augment)
 *   used to attach `libraryImageId` to each slide and flag the current banner by
 *   image ID. When omitted, banner detection falls back to the ES `TopBanner.Thumb`.
 * @returns An array of ImageModel objects for use in project tiles.
 */
export const getDirectoryTileImages = (
    item: SearchResultItem,
    bannerInfo?: ProjectBannerInfo,
): ImageModel[] => {
    // Banner detection, preferred → fallback:
    // 1. ID match via the GraphQL augment (crop-independent, also yields the
    //    per-slide LibraryImage ID needed to set a banner).
    // 2. Raw ES `TopBanner.Thumb` match (both come from the same thumbnail
    //    variant, so the `noindex` param is never in play).
    // We never guess by position.
    const bannerThumb = item.TopBanner?.Thumb;

    const images =
        item.DirectoryTiles?.filter((imageTile, idx, arr) => {
            if (!imageTile.Thumb) return false;
            // Remove duplicate images by Thumb
            return arr.findIndex((t) => t.Thumb === imageTile.Thumb) === idx;
        }).map((imageTile) => {
            const thumb = imageTile.Thumb as string;
            const libraryImageId = bannerInfo?.imageIdByThumb.get(
                normalizeImageKey(thumb),
            );
            const isBanner =
                libraryImageId != null && bannerInfo?.bannerImageIds.length
                    ? bannerInfo.bannerImageIds.includes(libraryImageId)
                    : bannerThumb != null && thumb === bannerThumb;

            return {
                src: appendNoIndexParam(thumb),
                alt: item.Title ?? '',
                multiplied: imageTile.IsMultiplied,
                isBanner,
                libraryImageId,
            };
        }) || [];

    // When the GraphQL augment ran we know the live image set; drop ES slides
    // whose thumb no longer maps to a LibraryImage (deleted on the item).
    const result = bannerInfo
        ? images.filter((image) => image.libraryImageId != null)
        : images;

    return moveBannerImageFirst(result);
};
